import { useEffect, useState } from 'react';

import { type AdsRunDetail, type AdsRunSummary, fetchAdsRun, fetchAdsRuns } from './api.ts';
import { describeError } from './data.tsx';
import { CreativeRequestFacts, creativeRequestStatusTone } from './CreativeRequests.tsx';
import { DecisionList } from './DecisionLog.tsx';
import { Icon } from './Icon.tsx';
import { adsReviewsPath, Link } from './router.tsx';
import { elapsed, localTime } from './schedule.ts';
import { Badge, EmptyState, Facts, Notice, Panel, Skeleton, type Tone } from './ui.tsx';

/**
 * The Reviews tab (meta-ads-agent US-017): every review the agent ran, and one
 * review in full — what it saw (the snapshot), what it answered (Claude's raw
 * output, its summary and observations) and what came of it (its decisions,
 * drawn with the decision log's own `DecisionList`, and its creative requests).
 * The server pretty-prints the stored JSON (`ads/runs.ts`).
 */

/** While a review is queued or running, the list looks again this often. */
const ACTIVE_POLL_MS = 10_000;

const STATUS_LABEL: Readonly<Record<AdsRunSummary['status'], string>> = {
  queued: 'Queued',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  held_usage_limit: 'Held by the usage limit',
};

const STATUS_TONE: Readonly<Record<AdsRunSummary['status'], Tone>> = {
  queued: 'neutral',
  running: 'active',
  succeeded: 'done',
  failed: 'danger',
  held_usage_limit: 'wait',
};

const TRIGGER_LABEL: Readonly<Record<AdsRunSummary['trigger'], string>> = {
  schedule: 'Schedule',
  manual: 'Manual',
  launch: 'Launch',
  retry: 'Retry',
};

export function RunHistoryView({ runId }: { readonly runId: string | null }) {
  return runId === null ? <RunList /> : <RunDetailView id={runId} />;
}

function isActive(run: AdsRunSummary): boolean {
  return run.status === 'queued' || run.status === 'running';
}

function RunStatus({ run }: { readonly run: AdsRunSummary }) {
  return (
    <Badge tone={STATUS_TONE[run.status]} pulse={run.status === 'running'}>
      {STATUS_LABEL[run.status]}
    </Badge>
  );
}

function duration(run: AdsRunSummary): string {
  if (run.durationMs !== null) return elapsed(run.durationMs);
  return isActive(run) ? 'running' : '—';
}

function model(run: AdsRunSummary): string {
  if (run.model === null) return 'default';
  return run.effort === null ? run.model : `${run.model} · ${run.effort}`;
}

/* ------------------------------------------------------------------ list */

function RunList() {
  const [runs, setRuns] = useState<AdsRunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const active = runs?.some(isActive) ?? false;

  useEffect(() => {
    const controller = new AbortController();
    const load = (): void => {
      fetchAdsRuns(controller.signal)
        .then((next) => {
          setRuns(next.runs);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (!controller.signal.aborted) setError(describeError(cause));
        });
    };
    load();
    const timer = active ? window.setInterval(load, ACTIVE_POLL_MS) : undefined;
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [active]);

  if (runs === null) return error === null ? <Skeleton lines={6} /> : <Notice kind="error">{error}</Notice>;
  if (runs.length === 0) {
    return (
      <EmptyState icon="clock" title="No reviews yet">
        The agent reviews the ads every 12 hours once it is enabled; <strong>Run review now</strong> starts one at any
        time.
      </EmptyState>
    );
  }
  return (
    <>
      {error !== null && <Notice kind="error">{error}</Notice>}
      <Panel title="Reviews" icon="clock" meta={<span className="muted">newest first</span>}>
        <div className="table-wrap">
          <table className="table table--cards ads-runs">
            <thead>
              <tr>
                <th>Started</th>
                <th>Trigger</th>
                <th>Status</th>
                <th className="num">Duration</th>
                <th className="num">Executed</th>
                <th className="num">Refused</th>
                <th className="num">Held</th>
                <th>Model</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td className="table__cell--lead">
                    <Link className="link--strong" href={adsReviewsPath(run.id)}>
                      {run.startedAt === null ? `Queued ${localTime(run.createdAt)}` : localTime(run.startedAt)}
                    </Link>
                  </td>
                  <td data-label="Trigger">{TRIGGER_LABEL[run.trigger]}</td>
                  <td data-label="Status">
                    <RunStatus run={run} />
                  </td>
                  <td data-label="Duration" className="num">
                    {duration(run)}
                  </td>
                  <td data-label="Executed" className="num">
                    {run.decisions.executed}
                  </td>
                  <td data-label="Refused" className="num">
                    {run.decisions.refused}
                  </td>
                  <td data-label="Held" className="num">
                    {run.decisions.held}
                  </td>
                  <td data-label="Model" className="mono">
                    {model(run)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}

/* ---------------------------------------------------------------- detail */

function RunDetailView({ id }: { readonly id: string }) {
  const [run, setRun] = useState<AdsRunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const active = run !== null && isActive(run);

  useEffect(() => {
    const controller = new AbortController();
    const load = (): void => {
      fetchAdsRun(id, controller.signal)
        .then((next) => {
          setRun(next);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (!controller.signal.aborted) setError(describeError(cause));
        });
    };
    load();
    const timer = active ? window.setInterval(load, ACTIVE_POLL_MS) : undefined;
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [id, active]);

  const back = (
    <Link className="button button--quiet" href={adsReviewsPath()}>
      <Icon name="arrow-left" />
      All reviews
    </Link>
  );

  if (run === null || run.id !== id) {
    return (
      <>
        <div className="toolbar">{back}</div>
        {error === null ? <Skeleton lines={6} /> : <Notice kind="error">{error}</Notice>}
      </>
    );
  }

  const { decisions } = run;
  return (
    <>
      <div className="toolbar">{back}</div>
      {error !== null && <Notice kind="error">{error}</Notice>}
      {run.status === 'failed' && (
        <Notice kind="error">
          <strong>This review failed.</strong> {run.error ?? 'No error was stored.'}
        </Notice>
      )}
      {run.status === 'held_usage_limit' && (
        <Notice kind="wait">
          <strong>Held by the Claude usage limit.</strong> {run.error ?? 'It runs again once the limit lifts.'}
        </Notice>
      )}
      <Panel
        title={`Review · ${localTime(run.startedAt ?? run.createdAt)}`}
        icon="comment"
        meta={<RunStatus run={run} />}
      >
        <Facts
          items={[
            { label: 'Trigger', value: TRIGGER_LABEL[run.trigger] },
            { label: 'Started', value: run.startedAt === null ? 'not yet' : localTime(run.startedAt) },
            { label: 'Finished', value: run.finishedAt === null ? '—' : localTime(run.finishedAt) },
            { label: 'Duration', value: duration(run) },
            { label: 'Model', value: model(run), mono: true },
            {
              label: 'Decisions',
              value: `${String(decisions.executed)} executed · ${String(decisions.refused)} refused · ${String(decisions.held)} held · ${String(decisions.failed)} failed`,
            },
          ]}
        />
        {run.summary !== null && run.summary.trim() !== '' && <p className="ads-run__summary">{run.summary}</p>}
        {run.observations.length > 0 && (
          <>
            <h3 className="ads-kpi__heading">Observations</h3>
            <ul className="ads-observations">
              {run.observations.map((observation, index) => (
                <li key={index}>{observation}</li>
              ))}
            </ul>
          </>
        )}
      </Panel>
      <Panel title="Decisions" icon="list" meta={<span className="muted">in the order it made them</span>}>
        <DecisionList decisions={run.decisionList} empty="This review made no decisions." />
      </Panel>
      <Panel title="Creative requests" icon="image">
        {run.creativeRequests.length === 0 ? (
          <p className="muted">This review asked for no new creative.</p>
        ) : (
          <ul className="ads-requests">
            {run.creativeRequests.map((request) => (
              <li key={request.id} className="ads-request">
                <div className="ads-request__head">
                  <strong>{request.concept}</strong>
                  <Badge tone={creativeRequestStatusTone(request.status)}>{request.status}</Badge>
                </div>
                <CreativeRequestFacts request={request} />
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel title="Raw data" icon="list">
        <RawJson title="Snapshot sent to Claude" text={run.snapshot} empty="No snapshot was stored." />
        <RawJson title="Claude's raw output" text={run.rawOutput} empty="Claude did not answer." />
      </Panel>
    </>
  );
}

function RawJson({ title, text, empty }: { readonly title: string; readonly text: string | null; readonly empty: string }) {
  if (text === null) {
    return (
      <p className="muted">
        {title}: {empty}
      </p>
    );
  }
  return (
    <details className="disclosure">
      <summary className="disclosure__summary">
        <Icon name="chevron-right" />
        {title}
      </summary>
      <div className="disclosure__body">
        <pre className="ads-run__json">{text}</pre>
      </div>
    </details>
  );
}
